import { useState } from 'react';
import { useApp } from '../context/AppContext';
import type { GameRecord } from '../../shared/types';

export default function History() {
  const { games, players, service } = useApp();
  const [editing, setEditing] = useState<{ gameId: string; courtIdx: number } | null>(null);
  const [editA, setEditA] = useState('');
  const [editB, setEditB] = useState('');

  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id;

  function startEdit(game: GameRecord, courtIdx: number) {
    const c = game.courts[courtIdx];
    setEditing({ gameId: game.id, courtIdx });
    setEditA(c.scoreA != null ? String(c.scoreA) : '');
    setEditB(c.scoreB != null ? String(c.scoreB) : '');
  }

  async function saveEdit(game: GameRecord) {
    if (!editing) return;
    if (editA === '' || editB === '') { alert('Please enter both scores.'); return; }
    const updated: GameRecord = {
      ...game,
      courts: game.courts.map((c, i) =>
        i === editing.courtIdx
          ? { ...c, scoreA: parseInt(editA), scoreB: parseInt(editB), edited: true }
          : c
      ),
    };
    await service.updateGame(updated);
    setEditing(null);
  }

  if (!games.length) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        <div className="empty">
          <div className="empty-icon">📋</div>
          No history yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      {[...games].reverse().map(game => (
        <div key={game.id} className="card" style={{ padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: '.78rem', color: 'var(--gray-500)', marginBottom: 8 }}>
            📅 {new Date(game.date).toLocaleString()} · Round {game.round}
          </div>

          {game.courts.map((court, i) => {
            const isEditing = editing?.gameId === game.id && editing?.courtIdx === i;
            return (
              <div key={i} className="history-item" style={i > 0 ? { marginTop: 6 } : {}}>
                <div className="history-header">
                  <div>
                    <strong>{court.teamA.map(playerName).join(' & ')}</strong>
                    <span style={{ color: 'var(--gray-500)' }}> vs </span>
                    <strong>{court.teamB.map(playerName).join(' & ')}</strong>
                    <div className="history-meta">
                      Court {i + 1}
                      {court.edited && <span style={{ color: '#b45309' }}> · edited</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {court.scoreA != null
                      ? <strong>{court.scoreA}–{court.scoreB}</strong>
                      : <span className="badge badge-gray">No score</span>
                    }
                    {!isEditing && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ padding: '3px 8px', fontSize: '.75rem' }}
                        onClick={() => startEdit(game, i)}
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div style={{ marginTop: 8, padding: 10, background: 'var(--gray-100)', borderRadius: 8 }}>
                    <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 8 }}>
                      Correct Score — Court {i + 1}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '.82rem' }}>Team A</span>
                      <input
                        type="number" min={0} max={99}
                        value={editA}
                        onChange={e => setEditA(e.target.value)}
                        className="score-input"
                        style={{ width: 56 }}
                        placeholder="0"
                      />
                      <span style={{ fontWeight: 700, color: 'var(--gray-500)' }}>–</span>
                      <input
                        type="number" min={0} max={99}
                        value={editB}
                        onChange={e => setEditB(e.target.value)}
                        className="score-input"
                        style={{ width: 56 }}
                        placeholder="0"
                      />
                      <span style={{ fontSize: '.82rem' }}>Team B</span>
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ marginLeft: 'auto' }}
                        onClick={() => saveEdit(game)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
